import React, { createContext, useCallback, useContext, useMemo } from "react";

import { useList } from "@refinedev/core";

import { useAllPractitionerRoles } from "@/hooks/useAllPractitionerRoles";
import { type SetCoverageResult, useCoverage } from "@/hooks/useCoverage";
import { useOrganizationPractitioners } from "@/hooks/useOrganizationPractitioners";
import { usePractitionerOrganization } from "@/hooks/usePractitionerOrganization";
import { FhirResourceType, extractFhirResourceId } from "@/utils/fhir-utils";

import { fhirR4 } from "@smile-cdr/fhirts";

interface PractitionerPageContextValue {
  // Organization
  organizationReference: string | undefined;
  isOrgLoading: boolean;

  // PractitionerRoles + Practitioners (scoped to current org)
  practitionerRoles: fhirR4.PractitionerRole[];
  practitionerResources: fhirR4.Practitioner[];
  isRolesLoading: boolean;

  // All PractitionerRoles across all orgs (for clinics display)
  allPractitionerRoles: fhirR4.PractitionerRole[];

  // Organizations (clinics)
  orgResources: fhirR4.Organization[];
  isOrgsLoading: boolean;

  // Coverage (from REST API)
  coverageMap: Map<string, string>;
  isCoverageLoading: boolean;
  setCoverage: (coveredPracId: string, coveringPracId: string) => Promise<SetCoverageResult>;
  removeCoverage: (coveredPracId: string, coveringPracId: string) => Promise<boolean>;

  // Refetch
  refetchAll: () => Promise<void>;
}

// Default no-op functions for createContext — only used if a consumer renders without a Provider ancestor.
const noop = async () => ({ success: false, conflict: false }) as SetCoverageResult;
const noopRemove = async () => false;

const PractitionerPageContext = createContext<PractitionerPageContextValue>({
  organizationReference: undefined,
  isOrgLoading: true,
  practitionerRoles: [],
  practitionerResources: [],
  isRolesLoading: true,
  allPractitionerRoles: [],
  orgResources: [],
  isOrgsLoading: true,
  coverageMap: new Map(),
  isCoverageLoading: true,
  setCoverage: noop,
  removeCoverage: noopRemove,
  refetchAll: async () => {},
});

export const PractitionerPageProvider = ({ children }: { children: React.ReactNode }) => {
  // 1) Organization reference
  const { organizationReference, isLoading: isOrgLoading } = usePractitionerOrganization();

  // 2) PractitionerRoles + Practitioners (with _include, already optimized)
  const {
    practitionerRoles,
    practitionerResources,
    isLoading: isRolesLoading,
    refetch: refetchRoles,
  } = useOrganizationPractitioners(organizationReference, { includeInactive: true });

  // 2b) All PractitionerRoles across all orgs (for clinics display)
  // Now includes Organizations via _include (eliminates separate org query)
  const {
    allPractitionerRoles,
    organizations: includedOrgs,
    isLoading: isAllRolesLoading,
    refetch: refetchAllRoles,
  } = useAllPractitionerRoles(practitionerResources);

  // Extract clinic IDs to check for any missing organizations
  const clinicOrgIds = useMemo(() => {
    const ids = [...practitionerRoles, ...allPractitionerRoles]
      .map(r => extractFhirResourceId(r.organization?.reference, FhirResourceType.Organization))
      .filter((x): x is string => !!x);
    return Array.from(new Set(ids));
  }, [practitionerRoles, allPractitionerRoles]);

  // Check for organizations not included (edge case: parent org from current org-scoped roles)
  const includedOrgIds = useMemo(() => new Set(includedOrgs.map(o => o.id).filter(Boolean)), [includedOrgs]);
  const missingOrgIds = useMemo(
    () => clinicOrgIds.filter(id => !includedOrgIds.has(id)),
    [clinicOrgIds, includedOrgIds]
  );

  // 3) Fetch any missing Organizations (fallback for edge cases) + parent org via _include
  const {
    result: orgsResult,
    query: { isLoading: isOrgsLoading, refetch: refetchOrgs },
  } = useList<fhirR4.Organization>({
    resource: FhirResourceType.Organization,
    filters:
      missingOrgIds.length > 0 && !!organizationReference
        ? [
            { field: "partof", operator: "eq", value: organizationReference },
            { field: "_id", operator: "eq", value: missingOrgIds.join(",") },
          ]
        : [],
    queryOptions: {
      enabled: missingOrgIds.length > 0 && !!organizationReference,
    },
    meta: {
      fetchAllPages: true,
    },
  });

  // Merge included organizations with any additionally fetched ones
  // Filter out parent org to prevent it from showing as a clinic option
  const orgResources = useMemo(() => {
    const additional = (orgsResult?.data as fhirR4.Organization[] | undefined) ?? [];
    const allOrgs = [...includedOrgs, ...additional];
    const parentOrgId = extractFhirResourceId(organizationReference, FhirResourceType.Organization);
    return allOrgs.filter(org => org.id !== parentOrgId);
  }, [includedOrgs, orgsResult?.data, organizationReference]);

  // Combined loading state for organizations
  const isCombinedOrgsLoading = isAllRolesLoading || isOrgsLoading;

  // 4) Coverage via REST API - only fetch for active practitioners
  const practitionerIds = useMemo(
    () => practitionerResources.filter(p => p.active !== false && p.id).map(p => p.id!),
    [practitionerResources]
  );

  const {
    coverageMap,
    isLoading: isCoverageLoading,
    setCoverage,
    removeCoverage,
    refetch: refetchCoverage,
  } = useCoverage({
    practitionerIds,
    enabled: practitionerIds.length > 0,
  });

  const refetchAll = useCallback(async () => {
    await Promise.all([refetchRoles(), refetchAllRoles(), refetchOrgs(), refetchCoverage()]);
  }, [refetchRoles, refetchAllRoles, refetchOrgs, refetchCoverage]);

  const value = useMemo(
    () => ({
      organizationReference,
      isOrgLoading,
      practitionerRoles,
      practitionerResources,
      isRolesLoading,
      allPractitionerRoles,
      orgResources,
      isOrgsLoading: isCombinedOrgsLoading,
      coverageMap,
      isCoverageLoading,
      setCoverage,
      removeCoverage,
      refetchAll,
    }),
    [
      organizationReference,
      isOrgLoading,
      practitionerRoles,
      practitionerResources,
      isRolesLoading,
      allPractitionerRoles,
      orgResources,
      isCombinedOrgsLoading,
      coverageMap,
      isCoverageLoading,
      setCoverage,
      removeCoverage,
      refetchAll,
    ]
  );

  return <PractitionerPageContext.Provider value={value}>{children}</PractitionerPageContext.Provider>;
};

export const usePractitionerPageContext = (): PractitionerPageContextValue => useContext(PractitionerPageContext);
